import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { useState } from "react";
import { activityVisuals, ChangeValue, fieldLabelFor } from "@/components/shared/activity-log";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { type AuditLogEntry, type FieldChange, useAuditLog } from "@/hooks/use-audit-log";
import { formatEffectiveDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

interface PersonTimelineProps {
  entityType: string;
  entityId: string;
  /** FK column → display-string resolver; forwarded to the value renderer. */
  fieldResolvers?: Record<string, (value: unknown) => string>;
  /** Field key → display label; forwarded to the change-line renderer. */
  fieldLabels?: Record<string, string>;
}

const MONTH_YEAR = new Intl.DateTimeFormat("en", { month: "long", year: "numeric" });
const DAY = new Intl.DateTimeFormat("en", { month: "short", day: "numeric" });

interface TimelineGroup {
  key: string;
  label: string;
  entries: AuditLogEntry[];
}

/**
 * Contiguous month buckets over the (already newest-first) audit stream.
 * Entries arrive ordered by created_at DESC, so a single pass that opens a new
 * group whenever the year-month changes preserves order without a re-sort.
 */
function groupByMonth(entries: AuditLogEntry[]): TimelineGroup[] {
  const groups: TimelineGroup[] = [];
  let current: TimelineGroup | null = null;
  for (const entry of entries) {
    const date = new Date(entry.createdAt);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    if (!current || current.key !== key) {
      current = { key, label: MONTH_YEAR.format(date), entries: [] };
      groups.push(current);
    }
    current.entries.push(entry);
  }
  return groups;
}

/**
 * Grouped, scannable rendering of a person's change history — the same audit
 * stream as `ActivityLog`, bucketed into collapsible month groups with a
 * vertical rail. Reads `/api/audit-log` via `useAuditLog` and keeps the
 * "Load more" cursor walk, folding new pages into the groups. Unlike the flat
 * list, it drops the actor line and renders each field change as a plain
 * statement ("Job title updated to …").
 */
export function PersonTimeline({
  entityType,
  entityId,
  fieldResolvers,
  fieldLabels,
}: PersonTimelineProps) {
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useAuditLog(
    entityType,
    entityId,
  );
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return <p className="text-sm text-muted-foreground">Activity unavailable.</p>;
  }

  const entries = data.pages.flatMap((page) => page.data);

  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">No activity recorded yet.</p>;
  }

  const groups = groupByMonth(entries);
  const lastEntryId = entries.at(-1)?.id;

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="space-y-5">
      {groups.map((group) => {
        const isCollapsed = collapsed.has(group.key);
        return (
          <section key={group.key} className="space-y-3">
            <button
              type="button"
              onClick={() => toggle(group.key)}
              aria-expanded={!isCollapsed}
              className="flex w-full items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
            >
              {isCollapsed ? (
                <ChevronRight className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5 shrink-0" />
              )}
              <span>{group.label}</span>
              <span className="font-normal normal-case">
                · {group.entries.length} {group.entries.length === 1 ? "change" : "changes"}
              </span>
            </button>
            {!isCollapsed && (
              <ol className="space-y-4">
                {group.entries.map((entry) => (
                  <TimelineEntry
                    key={entry.id}
                    entry={entry}
                    showConnector={entry.id !== lastEntryId}
                    fieldResolvers={fieldResolvers}
                    fieldLabels={fieldLabels}
                  />
                ))}
              </ol>
            )}
          </section>
        );
      })}
      {hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
        >
          {isFetchingNextPage && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
          {isFetchingNextPage ? "Loading…" : "Load more"}
        </Button>
      )}
    </div>
  );
}

function TimelineEntry({
  entry,
  showConnector,
  fieldResolvers,
  fieldLabels,
}: {
  entry: AuditLogEntry;
  showConnector: boolean;
  fieldResolvers?: Record<string, (value: unknown) => string>;
  fieldLabels?: Record<string, string>;
}) {
  const { dot } = activityVisuals(entry.action);
  return (
    <li className="flex gap-3">
      <div className="flex flex-col items-center">
        <span
          className={cn("mt-1 h-2.5 w-2.5 shrink-0 rounded-full ring-4 ring-background", dot)}
        />
        {showConnector && <span className="mt-1 w-px flex-1 bg-border" />}
      </div>
      <div className="flex-1 space-y-1 pb-1">
        <p className="text-xs text-muted-foreground">
          {DAY.format(new Date(entry.createdAt))}
          {entry.effectiveDate ? ` · effective ${formatEffectiveDate(entry.effectiveDate)}` : ""}
        </p>
        <ul className="space-y-0.5 text-sm">
          {entry.action === "created" && <li className="text-muted-foreground">Record created</li>}
          {entry.action === "deleted" && <li className="text-muted-foreground">Record deleted</li>}
          {entry.action === "updated" &&
            Array.isArray(entry.diff) &&
            (entry.diff as FieldChange[]).map((change) => (
              <li key={change.field}>
                <ChangeLine
                  change={change}
                  fieldResolvers={fieldResolvers}
                  fieldLabels={fieldLabels}
                />
              </li>
            ))}
        </ul>
        {entry.changeReasonName && (
          <p className="text-xs text-muted-foreground">
            Reason: <span className="font-medium text-foreground">{entry.changeReasonName}</span>
          </p>
        )}
      </div>
    </li>
  );
}

/** True for null/undefined, empty string, and empty arrays. */
function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === "") return true;
  return Array.isArray(value) && value.length === 0;
}

/**
 * One field change as a plain statement: "Job title updated to X" when there
 * was a prior value, "Status set to X" when the field was previously empty,
 * "Manager cleared" when the new value is empty. `canceled:<field>` entries
 * (withdrawn scheduled changes) render as their label alone.
 */
function ChangeLine({
  change,
  fieldResolvers,
  fieldLabels,
}: {
  change: FieldChange;
  fieldResolvers?: Record<string, (value: unknown) => string>;
  fieldLabels?: Record<string, string>;
}) {
  const label = fieldLabelFor(change.field, fieldLabels);

  if (change.field.startsWith("canceled:")) {
    return <span className="text-muted-foreground">{label}</span>;
  }

  if (isEmptyValue(change.after)) {
    return (
      <span>
        <span className="font-medium">{label}</span>{" "}
        <span className="text-muted-foreground">cleared</span>
      </span>
    );
  }

  const verb = isEmptyValue(change.before) ? "set to" : "updated to";
  return (
    <span>
      <span className="font-medium">{label}</span>{" "}
      <span className="text-muted-foreground">{verb}</span>{" "}
      <ChangeValue value={change.after} field={change.field} resolvers={fieldResolvers} />
    </span>
  );
}
